import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { panelPath } from '../config/paths';
import { useDebugMode } from '../hooks/useDebugMode';
import { useOnboarding } from '../hooks/useOnboarding';
import { CheckCircle2, Database, Languages, Layers, Plus, Rocket, X } from 'lucide-react';
import {
  getAppleSearchAdsDashboardSummary,
  getPublisherEvents,
  type AppleSearchAdsDashboardSummary,
  type PublisherEvent,
} from '../api/client';
import { PublisherEventListItem } from './publisherEventUI';

const DASHBOARD_EVENTS_LIMIT = 10;

const CHECKLIST_STEPS = [
  { key: 'step1', path: '/applications' },
  { key: 'step2', path: '/secrets' },
  { key: 'step3', path: '/applications' },
  { key: 'step4', path: '/applications' },
] as const;

const DevToolsPanel: React.FC = () => {
  const { t } = useTranslation();
  const hasDebugMode = useDebugMode();
  const { hasSeen: checklistDismissed, markSeen: dismissChecklist } = useOnboarding('dashboard-checklist');

  const [events, setEvents] = useState<PublisherEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);

  const [adsSummary, setAdsSummary] = useState<AppleSearchAdsDashboardSummary | null>(null);
  const [loadingAdsSummary, setLoadingAdsSummary] = useState(true);
  const [adsSummaryError, setAdsSummaryError] = useState<string | null>(null);

  const loadEvents = useCallback(async () => {
    setLoadingEvents(true);
    setEventsError(null);
    try {
      const res = await getPublisherEvents({ limit: DASHBOARD_EVENTS_LIMIT });
      setEvents(res.items ?? []);
    } catch (e) {
      setEventsError(e instanceof Error ? e.message : t('publishers.loadError'));
    } finally {
      setLoadingEvents(false);
    }
  }, [t]);

  const loadAdsSummary = useCallback(async () => {
    setLoadingAdsSummary(true);
    setAdsSummaryError(null);
    try {
      const res = await getAppleSearchAdsDashboardSummary();
      setAdsSummary(res);
    } catch (e) {
      setAdsSummaryError(e instanceof Error ? e.message : t('panel.appleSearchAdsSummaryError'));
    } finally {
      setLoadingAdsSummary(false);
    }
  }, [t]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    void loadAdsSummary();
  }, [loadAdsSummary]);

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('panel.dashboardTitle')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t('panel.dashboardSubtitle')}
          </p>
        </div>

        <Link
          to={panelPath('/applications')}
          className="flex items-center space-x-2 px-4 py-2 rounded-lg font-medium bg-primary-600 text-white hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>{t('panel.addApplicationShort')}</span>
        </Link>
      </div>

      {!checklistDismissed && (
        <div className="rounded-xl border border-primary-200 dark:border-primary-800/60 bg-gradient-to-br from-primary-50/60 to-white dark:from-primary-900/20 dark:to-gray-800 p-5 relative">
          <button
            type="button"
            onClick={dismissChecklist}
            className="absolute top-3 right-3 p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label={t('onboarding.dashboard.dismiss')}
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2 mb-2">
            <Rocket className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {t('onboarding.dashboard.checklistTitle')}
            </h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            {t('onboarding.dashboard.checklistBody')}
          </p>
          <ol className="space-y-2">
            {CHECKLIST_STEPS.map((step, i) => (
              <li key={step.key}>
                <Link
                  to={panelPath(step.path)}
                  className="flex items-center gap-2.5 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-primary-300 dark:hover:border-primary-700 hover:bg-primary-50/30 dark:hover:bg-primary-900/10 transition-colors"
                >
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 text-xs font-bold shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {t(`onboarding.dashboard.${step.key}`)}
                  </span>
                </Link>
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="card p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('panel.quickActions')}
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Link
            to={panelPath('/applications')}
            className="flex flex-col items-center p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <Layers className="h-8 w-8 text-primary-600 dark:text-primary-400 mb-2" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {t('panel.applicationsTitle')}
            </span>
          </Link>
          <Link
            to={panelPath('/storage')}
            className="flex flex-col items-center p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <Database className="h-8 w-8 text-primary-600 dark:text-primary-400 mb-2" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {t('nav.storage')}
            </span>
          </Link>
          {hasDebugMode && (
            <Link
              to={panelPath('/localization')}
              className="flex flex-col items-center p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <Languages className="h-8 w-8 text-primary-600 dark:text-primary-400 mb-2" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {t('nav.aiLocalization')}
              </span>
            </Link>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('panel.publisherTimelineTitle')}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {t('panel.publisherTimelineSubtitle')}
            </p>
          </div>
          <Link
            to={panelPath('/publishers')}
            className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline shrink-0"
          >
            {t('panel.publisherTimelineViewAll')}
          </Link>
        </div>
        {eventsError && (
          <div className="px-4 py-3 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20">
            {eventsError}
          </div>
        )}
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {loadingEvents && <li className="px-4 py-6 text-center text-gray-500 text-sm">…</li>}
          {!loadingEvents && events.length === 0 && (
            <li className="px-4 py-6 text-center text-gray-500 text-sm">
              {t('publishers.timelineEmpty')}
            </li>
          )}
          {events.map((ev) => (
            <PublisherEventListItem key={ev.id} event={ev} />
          ))}
        </ul>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('panel.appleSearchAdsSummaryTitle')}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {t('panel.appleSearchAdsSummarySubtitle')}
            </p>
          </div>
          <Link
            to={panelPath('/apple-search-ads')}
            className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline shrink-0"
          >
            {t('panel.publisherTimelineViewAll')}
          </Link>
        </div>
        {adsSummaryError && (
          <div className="px-4 py-3 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20">
            {adsSummaryError}
          </div>
        )}
        {loadingAdsSummary && !adsSummary && (
          <div className="px-4 py-6 text-center text-gray-500 text-sm">…</div>
        )}
        {adsSummary && !adsSummary.connected && (
          <div className="px-4 py-6 text-center text-gray-500 text-sm">
            {t('panel.appleSearchAdsSummaryEmpty')}
          </div>
        )}
        {adsSummary && adsSummary.connected && (
          <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">{t('panel.appleSearchAdsSpend')}</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white mt-0.5">
                {adsSummary.totalSpend.toFixed(2)}
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">{t('panel.appleSearchAdsInstalls')}</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white mt-0.5">
                {adsSummary.totalInstalls}
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">{t('panel.appleSearchAdsAvgCPA')}</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white mt-0.5">
                {adsSummary.avgCPA.toFixed(2)}
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">{t('panel.appleSearchAdsAvgTTR')}</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white mt-0.5">
                {(adsSummary.avgTTR * 100).toFixed(1)}%
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DevToolsPanel;
